import { useState, useEffect } from 'react';
import type { Portal, PortalSubmissao } from '../../types';
import PortalMensagensInternas from './PortalMensagensInternas';
import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  XMarkIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const API_BASE = (import.meta as any).env?.VITE_API_URL || '';

interface PortalSubmissoesProps {
  portal: Portal;
  onClose: () => void;
  currentUser?: {
    id: string;
    nome: string;
  };
}

const PortalSubmissoes = ({ portal, onClose, currentUser }: PortalSubmissoesProps) => {
  const [submissoes, setSubmissoes] = useState<PortalSubmissao[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<'todos' | 'pendentes' | 'nao_lidas' | 'com_chat'>('todos');
  const [busca, setBusca] = useState('');
  const [chatAberto, setChatAberto] = useState<PortalSubmissao | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  useEffect(() => {
    carregarSubmissoes();
  }, [portal.id]);

  const carregarSubmissoes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/portal-submissoes?portalId=${portal.id}`);
      const data = await response.json();
      
      if (data.success) {
        setSubmissoes(data.submissoes || []);
      }
    } catch (err) {
      console.error('Erro ao carregar submissões:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataStr: string) => {
    // Datas ISO gravadas sem fuso vem do servidor em UTC; sem o 'Z' o
    // navegador as lia como horario local e exibia 3h a mais.
    const bruto = (dataStr || '').trim();
    const temFuso = /(Z|[+-]\d{2}:?\d{2})$/.test(bruto.slice(10));
    const data = new Date(temFuso || !bruto.includes('T') ? bruto : bruto + 'Z');
    if (isNaN(data.getTime())) return dataStr;
    return data.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const config = {
      pendente: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon, label: 'Pendente' },
      processado: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon, label: 'Processado' },
      erro: { bg: 'bg-red-100', text: 'text-red-800', icon: ExclamationCircleIcon, label: 'Erro' },
      arquivado: { bg: 'bg-gray-100', text: 'text-gray-800', icon: DocumentTextIcon, label: 'Arquivado' }
    };
    const cfg = config[status as keyof typeof config] || config.pendente;
    const Icon = cfg.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${cfg.bg} ${cfg.text}`}>
        <Icon className="h-3 w-3" />
        {cfg.label}
      </span>
    );
  };

  const submissoesFiltradas = submissoes.filter(sub => {
    // Filtro por status
    if (filtro === 'pendentes' && sub.status !== 'pendente') return false;
    if (filtro === 'nao_lidas' && (sub.mensagensNaoLidas || 0) === 0) return false;
    if (filtro === 'com_chat' && (sub.totalMensagens || 0) === 0) return false;
    
    // Busca
    if (busca) {
      const termo = busca.toLowerCase();
      return (
        (sub.nomeExterno?.toLowerCase().includes(termo)) ||
        (sub.emailExterno?.toLowerCase().includes(termo)) ||
        (sub.protocolo?.toLowerCase().includes(termo))
      );
    }
    
    return true;
  });

  const totalNaoLidas = submissoes.reduce((acc, sub) => acc + (sub.mensagensNaoLidas || 0), 0);
  const totalComChat = submissoes.filter(sub => (sub.totalMensagens || 0) > 0).length;

  const gerarLinkAcompanhamento = (sub: PortalSubmissao) => {
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? window.location.origin
      : 'https://escalone.duckdns.org';
    return `${baseUrl}/portal/${portal.tokenPublico}?submissaoId=${sub.id}&t=${sub.tokenAcesso || ''}`;
  };

  const copiarLink = async (sub: PortalSubmissao) => {
    const link = gerarLinkAcompanhamento(sub);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiado(sub.id);
      setTimeout(() => setCopiado(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
      prompt('Copie o link:', link);
    }
  };

  const abrirAcompanhamento = (sub: PortalSubmissao) => {
    const link = gerarLinkAcompanhamento(sub);
    window.open(link, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Submissões - {portal.nome}
            </h3>
            <p className="text-sm text-gray-500">
              {submissoes.length} submissões total
              {totalNaoLidas > 0 && (
                <span className="ml-2 text-blue-600 font-medium">
                  • {totalNaoLidas} mensagens não lidas
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={carregarSubmissoes}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Atualizar"
            >
              <ArrowPathIcon className="h-5 w-5 text-gray-500" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, email ou protocolo..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {[
              { id: 'todos', label: 'Todos' },
              { id: 'pendentes', label: 'Pendentes' },
              { id: 'nao_lidas', label: 'Não lidas', badge: totalNaoLidas },
              { id: 'com_chat', label: 'Com chat', badge: totalComChat }
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFiltro(f.id as any)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                  filtro === f.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {f.label}
                {f.badge && f.badge > 0 && (
                  <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {f.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : submissoesFiltradas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nenhuma submissão encontrada</p>
              {busca && (
                <button 
                  onClick={() => setBusca('')}
                  className="text-blue-600 text-sm mt-2 hover:underline"
                >
                  Limpar busca
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {submissoesFiltradas.map((sub) => (
                <div 
                  key={sub.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${
                    (sub.mensagensNaoLidas || 0) > 0 ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Cabeçalho */}
                      <div className="flex items-center gap-2 mb-1">
                        {sub.protocolo && (
                          <span className="text-sm font-mono font-bold text-blue-600">
                            {sub.protocolo}
                          </span>
                        )}
                        {getStatusBadge(sub.status)}
                        {(sub.mensagensNaoLidas || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            <ChatBubbleLeftRightIcon className="h-3 w-3" />
                            {sub.mensagensNaoLidas} nova(s)
                          </span>
                        )}
                      </div>

                      {/* Nome e email */}
                      <div className="flex items-center gap-4 text-sm">
                        {sub.nomeExterno && (
                          <span className="font-medium text-gray-900">{sub.nomeExterno}</span>
                        )}
                        {sub.emailExterno && (
                          <span className="flex items-center gap-1 text-gray-500">
                            <EnvelopeIcon className="h-4 w-4" />
                            {sub.emailExterno}
                          </span>
                        )}
                      </div>

                      {/* Data */}
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                        <ClockIcon className="h-3 w-3" />
                        {formatarData(sub.createdAt as any)}
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-2">
                      {/* Abrir página de acompanhamento */}
                      <button
                        onClick={() => abrirAcompanhamento(sub)}
                        className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                        title="Ver página de acompanhamento"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      
                      {/* Copiar link de acompanhamento */}
                      <button
                        onClick={() => copiarLink(sub)}
                        className={`p-2 rounded-lg transition-colors ${
                          copiado === sub.id
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={copiado === sub.id ? 'Link copiado!' : 'Copiar link de acompanhamento'}
                      >
                        {copiado === sub.id ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <LinkIcon className="h-5 w-5" />
                        )}
                      </button>
                      
                      {/* Chat */}
                      <button
                        onClick={() => setChatAberto(sub)}
                        className={`p-2 rounded-lg transition-colors relative ${
                          (sub.mensagensNaoLidas || 0) > 0
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : (sub.totalMensagens || 0) > 0
                            ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 ring-1 ring-blue-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={(sub.totalMensagens || 0) > 0 ? `${sub.totalMensagens} mensagem(s)` : 'Abrir chat'}
                      >
                        <ChatBubbleLeftRightIcon className="h-5 w-5" />
                        {(sub.totalMensagens || 0) > 0 && (sub.mensagensNaoLidas || 0) === 0 && (
                          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">
                            {sub.totalMensagens}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Chat */}
      {chatAberto && (
        <PortalMensagensInternas
          submissaoId={chatAberto.id}
          portalId={portal.id}
          nomeExterno={chatAberto.nomeExterno}
          emailExterno={chatAberto.emailExterno}
          protocolo={chatAberto.protocolo}
          currentUser={currentUser}
          onClose={() => {
            setChatAberto(null);
            carregarSubmissoes(); // Recarregar para atualizar contadores
          }}
        />
      )}
    </div>
  );
};

export default PortalSubmissoes;
